import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/pages/admin/AdminShell";
import { toast } from "sonner";
import { Download } from "lucide-react";

export default function AdminTransactions() {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [mode, setMode] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/transactions?type=${type}&status=${status}&mode=${mode}&limit=300`)
      .then(({ data }) => { if (!cancelled) { setItems(data.items || []); setLoading(false); } })
      .catch((e) => { if (!cancelled) { toast.error(formatApiError(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [user, type, status, mode]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">Verifying access…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  return (
    <AdminShell>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-eyebrow text-[#4F4D45]">Platform-wide ledger</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">All transactions</h1>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/exports/transactions.csv?type=${type}&status=${status}&mode=${mode}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B3526] hover:bg-[#0F2517] text-white text-sm font-medium"
          data-testid="admin-tx-export-csv"
        >
          <Download className="w-4 h-4" /> Export CSV
        </a>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-tx-type" className="px-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm">
          {["all","deposit","withdraw","transfer_in","transfer_out","promo_bonus","admin_adjustment"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-tx-status" className="px-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm">
          {["all","completed","pending","failed"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="admin-tx-mode" className="px-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm">
          {["all","live","demo"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="mt-6 fv-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#4F4D45]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#4F4D45]">No transactions.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-tx-table">
            <thead className="bg-[#F0ECE0] text-left text-xs uppercase tracking-wider text-[#4F4D45]">
              <tr>
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">User</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Description</th>
                <th className="px-5 py-3">Mode</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D2CDBE]">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-[#F0ECE0]/60">
                  <td className="px-5 py-2 text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-5 py-2 font-mono text-xs">{t.user_id?.slice(-6)}</td>
                  <td className="px-5 py-2 capitalize">{(t.type || "").replace("_", " ")}</td>
                  <td className="px-5 py-2">{t.description}</td>
                  <td className="px-5 py-2 capitalize">{t.mode}</td>
                  <td className="px-5 py-2 capitalize">{t.status}</td>
                  <td className="px-5 py-2 text-right font-mono">${(t.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
